// The slice of the API V2 uses. Same server, same routes as V1; the types are
// copied from V1's client rather than imported, so the two front ends share
// nothing but the API.

export interface Task {
  id: string;
  area_id: string | null; area_name: string | null;
  value_id: string | null; value_name: string | null;
  title: string;
  tier: "must" | "should" | "nice" | null;
  why: string | null; next: string | null;
  state: "open" | "blocked" | "waiting" | "done" | "parked";
  due: string | null;
  mins: number | null; money: string | null; note: string | null;
  blocked: boolean; blocked_until: string | null;
  blocked_by: string[]; unblocks: string[];
  follow_up_on: string | null; parked_because: string | null;
  why_chain: string[]; breadcrumb: string;
  /** What to show as the next action; the API has already applied the presentation rules. */
  next_now: string | null;
  /** V3: the container it belongs to (null when loose in its area), its area's key, an appointment's time in Berlin (YYYY-MM-DDTHH:MM), and the triage inbox. */
  container_id: string | null; container_key: string | null; area_key: string | null;
  at_local: string | null; needs_triage: boolean;
}

/** What GET /tasks/:id adds to a task: the tasks it waits on and unblocks, and its log. */
export interface TaskDetail extends Task {
  blockers: Task[]; unblocking: Task[];
  history: { id: number; date: string; kind: string; text: string | null }[];
}

export interface Value { key: string; name: string; colour: string; sort: number }
export interface Area { id: string; name: string; sort: number; archived_at: string | null; key: string | null }
export interface Board { today: string; values: Value[]; areas: Area[]; tasks: Task[] }

export interface Travel { id: string; starts_on: string; ends_on: string; label: string; note: string | null }

export interface Flag {
  id: string; title: string; body: string | null;
  /** The named choices the flag is deciding between (0023). Null or empty on a flag made before that. */
  options?: string[] | null;
  state: "open" | "resolved"; resolution: string | null; tasks: string[];
}

export interface Today {
  date: string;
  /** Set while today falls inside a trip. The brief stops asking, and says why. */
  travel: Travel | null;
  heavy: (Task & { why_today: string | null }) | null;
  then: Task[];
  quiet_hour: { task: Task; value: string; last_touched: string | null } | null;
  flag: Flag | null;
  /** Null inside a travel range. */
  question: string | null;
  rhythms: string[];
  /** V3: tasks with a time today, earliest first. */
  appointments: Appointment[];
  /** V3: every active or waiting container, grouped by area. */
  working_on: WorkingOnGroup[];
}

export interface Appointment {
  id: string; title: string; state: string; next: string | null;
  container_key: string | null; area_key: string | null; area_name: string | null;
  /** HH:MM, Berlin. */
  time: string;
}

export type ContainerKind = "case" | "pursuit" | "project";
export type Lifecycle = "active" | "waiting" | "closed" | "archived";

/** A finite thing inside an area: a case, a pursuit or a project. */
export interface Container {
  id: string; key: string; name: string; kind: ContainerKind;
  area_id: string; area_key: string | null; area_name: string;
  status_line: string | null; waiting_on: string | null; lifecycle: Lifecycle;
  context_path: string | null; sort: number;
}

export interface ContainerTask {
  id: string; title: string; state: string; due: string | null; at: string | null; next: string | null; needs_triage: boolean;
}

export interface ContainerDetail extends Container { open_tasks: ContainerTask[] }

export interface WorkingOnGroup {
  area: { id: string; key: string | null; name: string };
  containers: ContainerDetail[];
}

export type AgentLight = "green" | "yellow" | "grey";
/** One registered agent. Read-only here: agents report through the MCP tools. */
export interface Agent {
  key: string; name: string; purpose: string | null;
  runs_where: "chat" | "routine" | "local" | "script";
  cadence_hours: number | null; state: "planned" | "live" | "retired";
  last_run_at: string | null; last_outcome: "ok" | "partial" | "failed" | null; last_note: string | null;
  light: AgentLight;
}

export interface HorizonItem extends Task { starts_now_for: { id: string; title: string; due: string | null }[] }
/** GET /horizon: what is dated in the next N days, and what has to start now for something that is. */
export interface Horizon {
  from: string; to: string;
  dated: Task[]; lead_time: HorizonItem[];
  flags: Flag[]; travel: Travel[];
}

export interface CalSource { id: string; label: string; colour: string }
export interface CalEvent {
  id: string; source: string; summary: string | null; location: string | null;
  all_day: boolean;
  /** Berlin dates and times. For an all-day event `end_day` is the day after the last one, as in iCal. */
  on_day: string; end_day: string; from_time: string; to_time: string;
}
export interface CalendarData { from: string; to: string; sources: CalSource[]; events: CalEvent[] }

export type LightState = "green" | "yellow" | "red";
/** One status light. `at` is when the fact it reports happened, if it has a time. */
export interface Light {
  key: string; label: string; state: LightState; detail: string; at: string | null;
  source: "internal" | "external";
}

/** A recorded morning/evening/weekly sitting. */
export interface Session { id: string; kind: "morning" | "evening" | "weekly"; date: string; wrote: unknown[] }

export class Unauthorised extends Error {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (res.status === 401) throw new Unauthorised("unauthorised");
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((body as any).error ?? "request failed"), { status: res.status });
  return body as T;
}

export const api = {
  me: () => call<{ authenticated: boolean }>("/auth/me"),
  login: (passphrase: string) => call<{ ok: true }>("/auth/login", { method: "POST", body: JSON.stringify({ passphrase }) }),
  logout: () => call<{ ok: true }>("/auth/logout", { method: "POST" }),

  today: () => call<Today>("/today"),
  board: () => call<Board>("/board"),
  horizon: (days = 21) => call<Horizon>(`/horizon?days=${days}`),
  calendar: (from: string, to: string) => call<CalendarData>(`/calendar?from=${from}&to=${to}`),
  task: (id: string) => call<TaskDetail>(`/tasks/${id}`),
  update: (id: string, patch: Record<string, unknown>) =>
    call(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  // Nothing is deleted. This is the nearest thing and it needs a reason.
  park: (id: string, because: string) =>
    call(`/tasks/${id}/park`, { method: "POST", body: JSON.stringify({ because }) }),

  resolveFlag: (id: string, resolution: string) =>
    call(`/flags/${id}/resolve`, { method: "POST", body: JSON.stringify({ resolution }) }),

  log: (entry: { kind: "note"; text: string }) => call(`/log`, { method: "POST", body: JSON.stringify(entry) }),
  /** Creates journal/YYYY-MM-DD-HHMMSS.md. Create only; the text is the whole entry. */
  journal: (text: string) => call<{ path: string; bytes: number; created: true }>("/journal", { method: "POST", body: JSON.stringify({ text }) }),
  status: () => call<{ checked_at: string; lights: Light[] }>("/status"),
  containers: () => call<Container[]>("/containers"),
  container: (key: string) => call<ContainerDetail>(`/containers/${encodeURIComponent(key)}`),
  agents: () => call<Agent[]>("/agents"),
  sessions: (limit = 7) => call<Session[]>(`/sessions?limit=${limit}`),
  recordSession: (body: { kind: "morning" | "evening" | "weekly"; transcript?: string | null; wrote?: unknown[]; date?: string }) =>
    call<Session>("/sessions", { method: "POST", body: JSON.stringify(body) }),
};
